import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { aiApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Sparkles, Loader2, Plus, History, Trash2, Database,
} from 'lucide-react';
import { cn } from '../lib/utils';
import TestoAi from '../components/ai/TestoAi';

const SUGGERIMENTI = [
  'Quanto ho speso di mozzarella il mese scorso?',
  'Quali prodotti sono aumentati più del 10%?',
  'Com’è andato il mese scorso rispetto a quello prima?',
  'Quanto pesa il personale sul venduto?',
  'Quali sono i miei primi 5 fornitori per spesa?',
];

const STRUMENTI_LABEL = {
  venduto_periodo: 'venduto del periodo',
  costi_periodo: 'costi del periodo',
  spesa_prodotti: 'acquisti per prodotto',
  aumenti_prezzi: 'aumenti di prezzo',
  venduto_voci: 'vendite per voce',
  costo_personale: 'costo del personale',
};

const AssistentePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stato, setStato] = useState(null);
  const [sessioni, setSessioni] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messaggi, setMessaggi] = useState([]);
  const [domanda, setDomanda] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [mostraStorico, setMostraStorico] = useState(false);
  const fine = useRef(null);

  const caricaSessioni = useCallback(() => {
    aiApi.getSessioni().then(setSessioni).catch(() => {});
  }, []);

  useEffect(() => {
    aiApi.getStato().then(setStato).catch(() => {});
    caricaSessioni();
  }, [caricaSessioni]);

  useEffect(() => {
    fine.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messaggi, attesa]);

  const apriSessione = async (id) => {
    try {
      setMessaggi(await aiApi.getMessaggi(id));
      setSessionId(id);
      setMostraStorico(false);
    } catch (e) {
      toast.error('Conversazione non disponibile');
    }
  };

  const nuova = () => {
    setSessionId(null);
    setMessaggi([]);
    setMostraStorico(false);
  };

  const elimina = async (id) => {
    try {
      await aiApi.eliminaSessione(id);
      if (id === sessionId) nuova();
      caricaSessioni();
      toast.success('Conversazione eliminata');
    } catch (e) {
      toast.error('Eliminazione non riuscita');
    }
  };

  const invia = async (testo) => {
    const q = (testo ?? domanda).trim();
    if (!q || attesa) return;
    setDomanda('');
    setMessaggi((prec) => [...prec, { ruolo: 'utente', testo: q }]);
    setAttesa(true);
    try {
      const res = await aiApi.chiedi(q, sessionId);
      setSessionId(res.session_id);
      setMessaggi((prec) => [...prec, {
        ruolo: 'assistente', testo: res.risposta, strumenti: res.strumenti, modello: res.modello,
      }]);
      caricaSessioni();
    } catch (e) {
      const messaggio = e.response?.data?.detail || 'L’assistente non ha risposto';
      setMessaggi((prec) => [...prec, { ruolo: 'assistente', testo: messaggio, errore: true }]);
      toast.error(messaggio);
    } finally {
      setAttesa(false);
    }
  };

  const domandaIniziale = useRef(location.state?.domanda || null);
  useEffect(() => {
    if (!domandaIniziale.current) return;
    const q = domandaIniziale.current;
    domandaIniziale.current = null;
    navigate(location.pathname, { replace: true, state: null });
    invia(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pb-4">
      <header className="glass-header sticky top-0 z-40 border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="ai-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold font-heading tracking-tight flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Assistente
            </h1>
            <p className="text-[11px] text-muted-foreground truncate" data-testid="ai-stato">
              {stato?.disponibile
                ? `ChatGPT ${stato.modello_rapido} sui tuoi dati`
                : 'Modulo AI non configurato'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMostraStorico((v) => !v)}
            data-testid="ai-storico-btn"
          >
            <History className="w-5 h-5" />
          </Button>
          <Button variant="outline" size="icon" onClick={nuova} data-testid="ai-nuova-conversazione">
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="px-4 py-4 max-w-lg mx-auto space-y-3">
        {mostraStorico && (
          <Card data-testid="ai-storico">
            <CardContent className="p-3 space-y-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Conversazioni recenti
              </p>
              {sessioni.length === 0 && (
                <p className="text-sm text-muted-foreground">Nessuna conversazione salvata.</p>
              )}
              {sessioni.map((s) => (
                <div key={s.session_id} className="flex items-center gap-2">
                  <button
                    onClick={() => apriSessione(s.session_id)}
                    className="min-w-0 flex-1 text-left py-1.5"
                    data-testid={`ai-sessione-${s.session_id}`}
                  >
                    <p className="text-sm font-medium truncate">{s.titolo}</p>
                    <p className="text-[11px] text-muted-foreground">{s.n_scambi} domande</p>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    onClick={() => elimina(s.session_id)}
                    data-testid={`ai-elimina-${s.session_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {messaggi.length === 0 && !attesa && (
          <Card data-testid="ai-benvenuto">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm">
                Chiedi quello che vuoi su <strong>venduto</strong>, <strong>acquisti</strong>,
                {' '}<strong>uscite di cassa</strong> e <strong>costo del personale</strong>.
                I numeri vengono letti dal tuo archivio, non inventati.
              </p>
              {stato?.copertura && (
                <p className="text-[11px] text-muted-foreground">
                  Venduto {stato.copertura.venduto?.da || '—'} → {stato.copertura.venduto?.a || '—'} ·
                  {' '}Fatture {stato.copertura.acquisti?.da || '—'} → {stato.copertura.acquisti?.a || '—'} ·
                  {' '}Libro paga: {stato.copertura.mesi_libro_paga?.length || 0} mesi
                </p>
              )}
              <div className="space-y-1.5">
                {SUGGERIMENTI.map((s, i) => (
                  <button
                    key={s}
                    onClick={() => invia(s)}
                    className="w-full text-left text-sm px-3 py-2 rounded-lg border hover:border-primary/50 transition-colors"
                    data-testid={`ai-suggerimento-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3" data-testid="ai-conversazione">
          {messaggi.map((m, i) => (
            <div key={i} className={cn('flex', m.ruolo === 'utente' ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2.5',
                  m.ruolo === 'utente'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : m.errore
                      ? 'bg-red-50 border border-red-200 rounded-bl-sm'
                      : 'bg-secondary rounded-bl-sm'
                )}
                data-testid={`ai-messaggio-${m.ruolo}-${i}`}
              >
                {m.ruolo === 'utente'
                  ? <p className="text-sm leading-relaxed">{m.testo}</p>
                  : <TestoAi testo={m.testo} />}
                {m.strumenti?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2">
                    {[...new Set(m.strumenti.map((s) => s.strumento))].map((s) => (
                      <Badge key={s} variant="outline" className="text-[10px] gap-1">
                        <Database className="w-2.5 h-2.5" />
                        {STRUMENTI_LABEL[s] || s}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {attesa && (
            <div className="flex justify-start" data-testid="ai-attesa">
              <div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Sto leggendo i tuoi dati…</span>
              </div>
            </div>
          )}
          <div ref={fine} className="h-40" />
        </div>
      </main>

      <div
        className="fixed left-0 right-0 z-40 border-t border-border bg-white"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-lg mx-auto px-4 py-3 flex items-end gap-2">
          <Textarea
            value={domanda}
            onChange={(e) => setDomanda(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                invia();
              }
            }}
            placeholder="Scrivi la tua domanda…"
            rows={1}
            className="min-h-[44px] max-h-32 resize-none"
            data-testid="ai-input"
          />
          <Button
            size="icon"
            className="h-11 w-11 shrink-0"
            onClick={() => invia()}
            disabled={attesa || !domanda.trim()}
            data-testid="ai-invia"
          >
            {attesa ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssistentePage;
